import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Music, Upload, Trash2, Youtube, X, Loader2, Search } from 'lucide-react';

/**
 * PeticionEditDialog — Modal único de edición de una petición.
 * Reemplaza la cadena de window.prompt() secuenciales por una UI profesional.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - peticion (obj)
 *  - playlistId (string)
 *  - api (string)  // REACT_APP_BACKEND_URL + /api
 *  - onSaved ()    // callback tras guardar OK (para refrescar la playlist)
 */
export const PeticionEditDialog = ({ open, onClose, peticion, playlistId, api, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [titulo, setTitulo] = useState('');
  const [artista, setArtista] = useState('');
  const [nota, setNota] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const fileInputRef = useRef(null);
  // Búsqueda Spotify inline
  const [searchingSpotify, setSearchingSpotify] = useState(false);
  const [spotifyResults, setSpotifyResults] = useState([]);

  // Cargar datos iniciales cuando se abre con una petición nueva
  useEffect(() => {
    if (open && peticion) {
      setTitulo(peticion.title || peticion.titulo || '');
      setArtista(peticion.artist || peticion.artista || '');
      setNota(peticion.nota || '');
      setYoutubeUrl(peticion.youtube_url || '');
      setSpotifyUrl(peticion.spotify_url || peticion.spotify_uri || '');
      setSpotifyResults([]);
    }
  }, [open, peticion]);

  const handleSearchSpotify = async () => {
    const q = [titulo, artista].filter(Boolean).join(' ').trim();
    if (!q) {
      toast.error('Introduce al menos un título o artista para buscar');
      return;
    }
    setSearchingSpotify(true);
    try {
      const r = await axios.get(`${api}/spotify/public/search`, {
        params: { query: q },
        headers: { Authorization: `Bearer ${token()}` },
      });
      const top3 = (r.data?.tracks || []).slice(0, 3);
      if (top3.length === 0) {
        toast.info('Sin resultados en Spotify');
      }
      setSpotifyResults(top3);
    } catch (err) {
      console.error('Spotify search error:', err);
      toast.error(`Error buscando: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSearchingSpotify(false);
    }
  };

  const applySpotifyTrack = (track) => {
    setSpotifyUrl(track.spotify_url || `https://open.spotify.com/track/${track.id}`);
    // Si los campos título/artista están vacíos, autorrellenar con los de Spotify
    if (!titulo) setTitulo(track.name || '');
    if (!artista) {
      setArtista((track.artists || []).map((a) => a.name).join(', '));
    }
    setSpotifyResults([]);
    toast.success('Spotify asignado');
  };

  const hasAudio = Boolean(peticion?.audio_file_id);

  const token = () => localStorage.getItem('token');

  const handleSave = async () => {
    if (!peticion?.id) return;
    setSaving(true);
    try {
      await axios.patch(
        `${api}/playlists/${playlistId}/peticiones/${peticion.id}`,
        {
          titulo: titulo,
          artista: artista,
          nota: nota,
          youtube_url: youtubeUrl || null,
          spotify_url: spotifyUrl || null,
        },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success('Petición actualizada');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Edit error:', err);
      toast.error(`No se pudo guardar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceAudio = async (file) => {
    if (!file || !peticion?.id) return;
    const ok = window.confirm(
      `¿Reemplazar el audio actual con "${file.name}"?\nEl archivo anterior se eliminará.`
    );
    if (!ok) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(
        `${api}/playlists/${playlistId}/peticiones/${peticion.id}/replace-audio`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token()}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      toast.success('Audio reemplazado');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Replace audio error:', err);
      toast.error(`Error: ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAudio = async () => {
    if (!peticion?.id) return;
    const ok = window.confirm('¿Eliminar el audio de esta petición? (Se conservará el link de YouTube/Spotify si lo hay.)');
    if (!ok) return;
    setSaving(true);
    try {
      await axios.patch(
        `${api}/playlists/${playlistId}/peticiones/${peticion.id}`,
        { clear_audio: true },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      toast.success('Audio eliminado');
      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Clear audio error:', err);
      toast.error(`No se pudo eliminar: ${err.response?.data?.detail || err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving && !uploading) onClose?.(); }}>
      <DialogContent
        className="bg-slate-900 border-slate-700 text-white max-w-lg w-[95vw] p-0 overflow-hidden flex flex-col max-h-[90vh]"
        data-testid="peticion-edit-dialog"
      >
        <DialogHeader className="shrink-0 px-4 py-3 border-b border-slate-700">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Music className="w-5 h-5 text-sky-400" />
            Editar petición
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-4 py-3 space-y-4">
          <div>
            <Label htmlFor="pet-edit-title" className="text-xs text-slate-400 mb-1 block">Título</Label>
            <Input
              id="pet-edit-title"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              placeholder="Ej. Canción – entrada novios"
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="peticion-edit-title-input"
            />
          </div>
          <div>
            <Label htmlFor="pet-edit-artist" className="text-xs text-slate-400 mb-1 block">Artista</Label>
            <Input
              id="pet-edit-artist"
              value={artista}
              onChange={(e) => setArtista(e.target.value)}
              placeholder="Ej. Adele"
              className="bg-slate-800 border-slate-600 text-white"
              data-testid="peticion-edit-artist-input"
            />
          </div>
          <div>
            <Label htmlFor="pet-edit-yt" className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
              <Youtube className="w-3 h-3 text-red-400" />
              Link YouTube (opcional)
            </Label>
            <Input
              id="pet-edit-yt"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://youtube.com/watch?v=..."
              className="bg-slate-800 border-slate-600 text-white text-xs"
              data-testid="peticion-edit-youtube-input"
            />
          </div>
          <div>
            <Label htmlFor="pet-edit-sp" className="text-xs text-slate-400 mb-1 block flex items-center gap-1">
              <Music className="w-3 h-3 text-green-400" />
              Link Spotify (opcional)
            </Label>
            <div className="flex gap-2">
              <Input
                id="pet-edit-sp"
                value={spotifyUrl}
                onChange={(e) => setSpotifyUrl(e.target.value)}
                placeholder="https://open.spotify.com/track/..."
                className="bg-slate-800 border-slate-600 text-white text-xs flex-1"
                data-testid="peticion-edit-spotify-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSearchSpotify}
                disabled={searchingSpotify}
                className="border-green-700 text-green-300 hover:bg-green-900/30 shrink-0"
                title="Buscar en Spotify por título + artista"
                data-testid="peticion-edit-spotify-search-btn"
              >
                {searchingSpotify ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <><Search className="w-3 h-3 sm:mr-1" /><span className="hidden sm:inline">Buscar</span></>
                )}
              </Button>
            </div>
            {/* Resultados de búsqueda Spotify (top 3) */}
            {spotifyResults.length > 0 && (
              <div className="mt-2 space-y-1 border border-green-700/40 rounded p-2 bg-green-900/10">
                <p className="text-[10px] text-green-300 uppercase tracking-wide mb-1">Resultados Spotify:</p>
                {spotifyResults.map((track) => {
                  const img = track.image_url || track.album?.images?.[0]?.url;
                  return (
                  <button
                    key={track.id || track.uri}
                    type="button"
                    onClick={() => applySpotifyTrack(track)}
                    className="w-full text-left flex items-center gap-2 p-1.5 rounded hover:bg-green-900/30 transition-colors"
                    data-testid={`peticion-edit-spotify-result-${track.id}`}
                  >
                    {img && (
                      <img src={img} alt="" className="w-10 h-10 rounded shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white truncate">{track.name}</p>
                      <p className="text-[10px] text-slate-400 truncate">
                        {(track.artists || []).map((a) => a.name).join(', ')}
                      </p>
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="pet-edit-nota" className="text-xs text-slate-400 mb-1 block">Nota interna (solo DJ)</Label>
            <Textarea
              id="pet-edit-nota"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej. Arrancar directo al coro..."
              rows={2}
              className="bg-slate-800 border-slate-600 text-white text-sm"
              data-testid="peticion-edit-nota-input"
            />
          </div>

          {/* Sección audio: reemplazar / eliminar */}
          {hasAudio && (
            <div className="border-t border-slate-700 pt-3 space-y-2">
              <Label className="text-xs text-slate-400 flex items-center gap-1">
                <Music className="w-3 h-3 text-purple-400" />
                Audio actual
                {peticion?.file_size_mb && (
                  <span className="text-slate-500 font-normal">· {peticion.file_size_mb} MB</span>
                )}
              </Label>
              <div className="flex gap-2 flex-wrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="border-purple-700 text-purple-300 hover:bg-purple-900/30"
                  data-testid="peticion-edit-replace-audio-btn"
                >
                  {uploading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
                  Reemplazar audio
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteAudio}
                  disabled={saving}
                  className="border-red-700 text-red-300 hover:bg-red-900/30"
                  data-testid="peticion-edit-delete-audio-btn"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Eliminar audio
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/m4a,audio/aac,.mp3,.m4a"
                  style={{ display: 'none' }}
                  onChange={(e) => handleReplaceAudio(e.target.files?.[0])}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 px-4 py-3 border-t border-slate-700 flex gap-2 sm:justify-end safe-bottom">
          <Button
            type="button"
            variant="ghost"
            onClick={() => !saving && !uploading && onClose?.()}
            disabled={saving || uploading}
            className="text-slate-300 hover:bg-slate-800"
            data-testid="peticion-edit-cancel-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving || uploading}
            className="bg-sky-600 hover:bg-sky-700"
            data-testid="peticion-edit-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PeticionEditDialog;
